import { createContext, type ReactNode, useContext } from 'react'

import { useCockpitRemotePartner } from '../../components/cockpitSectionNavigation'

/**
 * The way out of an opened status.
 *
 * A detail card is drawn over the tile board, and half of what its sections offer takes the reader
 * somewhere else on the page — the financial tab, the initiatives plan, the section a row names.
 * Navigating with the card still open leaves them looking at the list they just left, so a section
 * that navigates closes the card first.
 *
 * It is a context rather than a prop on every section: the sections are picked by status bit in one
 * switch, and threading the same callback through each of them would be nine props for one act.
 */
const AttentionDetailExitContext = createContext<() => void>(() => {})

interface IAttentionDetailExitProviderProps {
  /** What closing the card does — the expanded tile's own collapse. */
  onExit: () => void
  children: ReactNode
}

export const AttentionDetailExitProvider = ({
  onExit,
  children,
}: IAttentionDetailExitProviderProps) => (
  <AttentionDetailExitContext.Provider value={onExit}>
    {children}
  </AttentionDetailExitContext.Provider>
)

AttentionDetailExitProvider.displayName = 'AttentionDetailExitProvider'

const stayOnDetail = () => undefined

/**
 * Closes the opened status. Called by a section right after it navigates away; outside a detail it
 * does nothing, so a section drawn on its own stays safe to render. A section scoped to a partner
 * of the manager cockpit opens that partner's page in a tab of its own, so the card stays open.
 */
export const useAttentionDetailExit = () => {
  const exit = useContext(AttentionDetailExitContext)
  const remotePartner = useCockpitRemotePartner()

  return remotePartner ? stayOnDetail : exit
}
